import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { debounce } from 'lodash';

// URL parameter schema - defines what parameters are valid and their types
const URL_SCHEMA = {
  activity: { type: 'string', default: '' },
  location: { type: 'string', default: '' },
  locationPlaceId: { type: 'string', default: '' },
  guests: { type: 'number', default: null },
  minPrice: { type: 'number', default: 0 },
  maxPrice: { type: 'number', default: 3000 },
  tags: { type: 'array', default: [] },
  groupSize: { type: 'string', default: '' },
  creatives: { type: 'array', default: [] },
  bookingOptions: { type: 'array', default: [] },
  activeOption: { type: 'string', default: 'Alle' },
  page: { type: 'number', default: 1 },
  showMap: { type: 'boolean', default: false }
};

// Utility functions for URL handling
const parseURLParam = (value, type) => {
  if (!value) return null;
  
  try {
    switch (type) {
      case 'number':
        const num = parseInt(value, 10);
        return isNaN(num) ? null : num;
      case 'array':
        return value.split(',').filter(item => item.trim());
      case 'boolean':
        return value === 'true';
      case 'string':
        return value;
      default:
        return value;
    }
  } catch (e) {
    console.warn('Failed to parse URL param:', value, type);
    return null;
  }
};

const serializeURLParam = (value, type) => {
  if (value === null || value === undefined) return null;
  
  switch (type) {
    case 'array':
      return Array.isArray(value) && value.length > 0 ? value.join(',') : null;
    case 'number':
      return value !== null ? value.toString() : null;
    case 'boolean':
      return value === true ? 'true' : null;
    case 'string':
      return value || null;
    default:
      return value ? value.toString() : null;
  }
};

export const useSearchURLState = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isInitialLoad = useRef(true);
  const lastValidURL = useRef('');
  
  // Parse URL parameters into state
  const parseURLToState = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const state = {};
    
    Object.entries(URL_SCHEMA).forEach(([key, { type, default: defaultValue }]) => {
      const rawValue = params.get(key);
      const parsedValue = parseURLParam(rawValue, type);
      state[key] = parsedValue !== null ? parsedValue : defaultValue;
    });
    
    return state;
  }, [location.search]);

  const [urlState, setUrlState] = useState(parseURLToState);
  
  // Validate URL parameters
  const validateURLState = useCallback((state) => {
    const errors = [];
    
    // Add custom validation rules
    if (state.minPrice && state.maxPrice && state.minPrice > state.maxPrice) {
      errors.push('minPrice cannot be greater than maxPrice');
    }
    
    if (state.page && state.page < 1) {
      errors.push('page must be greater than 0');
    }
    
    return errors;
  }, []);

  // Debounced URL update function
  const debouncedUpdateURL = useCallback(
    debounce((newState, options = {}) => {
      const { replace = false, resetPage = false } = options;
      
      // Reset page if specified
      if (resetPage) {
        newState = { ...newState, page: 1 };
      }
      
      // Validate the new state
      const errors = validateURLState(newState);
      if (errors.length > 0) {
        console.warn('URL state validation errors:', errors);
        return;
      }
      
      // Serialize state to URL parameters
      const params = new URLSearchParams();
      
      Object.entries(newState).forEach(([key, value]) => {
        const { type } = URL_SCHEMA[key] || { type: 'string' };
        const serialized = serializeURLParam(value, type);
        
        if (serialized !== null) {
          params.set(key, serialized);
        }
      });
      
      const newURL = `/search${params.toString() ? `?${params.toString()}` : ''}`;
      
      // Prevent unnecessary navigations
      if (newURL === lastValidURL.current) {
        return;
      }
      
      lastValidURL.current = newURL;
      
      try {
        navigate(newURL, { 
          replace,
          state: { timestamp: Date.now() }
        });
      } catch (error) {
        console.error('Navigation error:', error);
        // Fallback to last valid URL
        if (lastValidURL.current) {
          navigate(lastValidURL.current, { replace: true });
        }
      }
    }, 300),
    [navigate, validateURLState]
  );

  // Update URL state
  const updateURLState = useCallback((updates, options = {}) => {
    setUrlState(prevState => {
      const newState = { ...prevState, ...updates };
      
      // Don't update URL on initial load
      if (!isInitialLoad.current) {
        debouncedUpdateURL(newState, options);
      }
      
      return newState;
    });
  }, [debouncedUpdateURL]);

  // Reset filters
  const resetFilters = useCallback(() => {
    const resetState = Object.fromEntries(
      Object.entries(URL_SCHEMA).map(([key, { default: defaultValue }]) => [key, defaultValue])
    );
    
    // Preserve activity and location
    resetState.activity = urlState.activity;
    resetState.location = urlState.location;
    resetState.locationPlaceId = urlState.locationPlaceId;
    
    updateURLState(resetState, { replace: true, resetPage: true });
  }, [urlState.activity, urlState.location, urlState.locationPlaceId, updateURLState]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const newState = parseURLToState();
    
    // Only update if state actually changed
    if (JSON.stringify(newState) !== JSON.stringify(urlState)) {
      setUrlState(newState);
    }
    
    isInitialLoad.current = false;
  }, [location.search, parseURLToState]);

  // Error recovery - handle malformed URLs
  useEffect(() => {
    try {
      const errors = validateURLState(urlState);
      if (errors.length > 0) {
        console.warn('Detected invalid URL state, redirecting to safe state');
        resetFilters();
      }
    } catch (error) {
      console.error('URL state error:', error);
      resetFilters();
    }
  }, [urlState, validateURLState, resetFilters]);

  return {
    urlState,
    updateURLState,
    resetFilters,
    isValidState: validateURLState(urlState).length === 0
  };
};
